import type { ReactNode } from 'react';
import { Area, AreaChart, Bar, BarChart, ResponsiveContainer } from 'recharts';
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SparklineProps {
  /** Series values, oldest → newest. */
  data: number[];
  /** CSS colour (default the admin violet). */
  color?: string;
  type?: 'line' | 'bar';
  height?: number;
  /** Highlight the last bar/point (default true). */
  emphasizeLast?: boolean;
  className?: string;
}

/** Tiny, axis-less trend chart for KPI cards and table cells. */
export function Sparkline({
  data,
  color = 'hsl(var(--adm-violet))',
  type = 'line',
  height = 40,
  emphasizeLast = true,
  className,
}: SparklineProps) {
  const rows = data.map((value, index) => ({ index, value }));
  const gradientId = `adm-spark-${Math.abs(hashSeries(data))}-${type}`;
  const allZero = data.every((v) => v === 0);

  return (
    <div className={cn('w-full', className)} style={{ height }} aria-hidden="true">
      <ResponsiveContainer width="100%" height="100%">
        {type === 'bar' ? (
          <BarChart data={rows} margin={{ top: 2, right: 0, bottom: 0, left: 0 }} barCategoryGap="22%">
            <Bar
              dataKey="value"
              radius={[3, 3, 1, 1]}
              isAnimationActive={false}
              minPointSize={allZero ? 2 : 2}
              shape={(props: unknown) => {
                const p = props as { x: number; y: number; width: number; height: number; index: number };
                const isLast = emphasizeLast && p.index === rows.length - 1;
                return (
                  <rect
                    x={p.x}
                    y={p.y}
                    width={p.width}
                    height={Math.max(p.height, 2)}
                    rx={2.5}
                    fill={color}
                    fillOpacity={isLast ? 1 : 0.28}
                  />
                );
              }}
            />
          </BarChart>
        ) : (
          <AreaChart data={rows} margin={{ top: 4, right: 3, bottom: 2, left: 3 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.22} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={1.75}
              fill={`url(#${gradientId})`}
              isAnimationActive={false}
              dot={(props: unknown) => {
                const p = props as { cx: number; cy: number; index: number };
                if (!emphasizeLast || p.index !== rows.length - 1) return <g key={p.index} />;
                return <circle key={p.index} cx={p.cx} cy={p.cy} r={2.75} fill="hsl(var(--card))" stroke={color} strokeWidth={1.75} />;
              }}
            />
          </AreaChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

function hashSeries(data: number[]) {
  return data.reduce((acc, v, i) => (acc * 31 + Math.round(v * 100) + i) | 0, 7);
}

export interface StatCardProps {
  label: ReactNode;
  value: ReactNode;
  /** Percentage change vs the comparison period. null/undefined hides the badge. */
  delta?: number | null;
  /** Caption after the delta, e.g. "vs last week". */
  deltaLabel?: ReactNode;
  /** Small line under the value when there is no delta (or in addition). */
  hint?: ReactNode;
  /** Series for the sparkline (oldest → newest). */
  trend?: number[];
  trendType?: 'line' | 'bar';
  /** Sparkline colour. */
  color?: string;
  /** Small icon in the label row. */
  icon?: ReactNode;
  /** Optional corner action (e.g. an arrow link). */
  action?: ReactNode;
  className?: string;
  'data-testid'?: string;
}

/** KPI card: label, big number, delta chip and a sparkline. */
export function StatCard({
  label,
  value,
  delta,
  deltaLabel,
  hint,
  trend,
  trendType = 'line',
  color,
  icon,
  action,
  className,
  ...rest
}: StatCardProps) {
  const hasDelta = typeof delta === 'number' && Number.isFinite(delta);
  const direction = !hasDelta || delta === 0 ? 'flat' : delta! > 0 ? 'up' : 'down';
  const DeltaIcon = direction === 'up' ? ArrowUpRight : direction === 'down' ? ArrowDownRight : Minus;

  return (
    <div className={cn('adm-panel flex min-w-0 flex-col p-4', className)} {...rest}>
      <div className="flex items-center justify-between gap-2">
        <p className="flex min-w-0 items-center gap-1.5 truncate text-[12.5px] font-medium text-muted-foreground [&_svg]:h-3.5 [&_svg]:w-3.5">
          {icon}
          <span className="truncate">{label}</span>
        </p>
        {action}
      </div>
      <div className="mt-2 flex items-end justify-between gap-3">
        <div className="min-w-0">
          <p className="adm-num truncate text-[24px] font-semibold leading-none text-foreground">{value}</p>
          <div className="mt-2 flex min-h-[20px] flex-wrap items-center gap-1.5 text-[11.5px] text-muted-foreground">
            {hasDelta && (
              <span className={cn('adm-delta', `adm-delta--${direction}`)}>
                <DeltaIcon className="h-3 w-3" strokeWidth={2.5} />
                {delta! > 0 ? '+' : ''}
                {Math.round(delta!)}%
              </span>
            )}
            {hasDelta && deltaLabel && <span className="truncate">{deltaLabel}</span>}
            {hint && <span className="truncate">{hint}</span>}
          </div>
        </div>
        {trend && trend.length > 1 && (
          <Sparkline data={trend} type={trendType} color={color} className="w-[96px] shrink-0" height={42} />
        )}
      </div>
    </div>
  );
}
